"use client";

import { c as _c } from "react/compiler-runtime";
import * as DropdownMenuPrimitives from "@radix-ui/react-dropdown-menu";
import * as React from "react";
import { RiArrowLeftSLine, RiArrowRightSLine, RiCheckLine, RiCheckboxBlankCircleLine, RiMore2Fill, RiRadioButtonFill, RiSearchLine } from "react-icons/ri";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { cx } from "@/lib/tailwind-utils";
const DropdownMenu = DropdownMenuPrimitives.Root;
DropdownMenu.displayName = "DropdownMenu";
const DropdownMenuTrigger = DropdownMenuPrimitives.Trigger;
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";
const DropdownMenuGroup = DropdownMenuPrimitives.Group;
DropdownMenuGroup.displayName = "DropdownMenuGroup";
const DropdownMenuSubMenu = DropdownMenuPrimitives.Sub;
DropdownMenuSubMenu.displayName = "DropdownMenuSubMenu";
const DropdownMenuRadioGroup = DropdownMenuPrimitives.RadioGroup;
DropdownMenuRadioGroup.displayName = "DropdownMenuRadioGroup";
const stopRadixMenuKeyNavFromTextEntry = (e: React.KeyboardEvent<HTMLElement>) => {
  // Radix DropdownMenu interprets keyboard events for menu navigation and typeahead.
  // If focus is inside a text-entry element within the menu, we want the input
  // to handle these events instead of Radix.
  const target = e.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const isTextEntry = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

  // Check if the target or any ancestor handles its own keyboard navigation
  // Use data-handles-arrow-keys attribute to opt-in to stopping arrow key propagation
  const handlesOwnNavigation = target.closest("[data-handles-arrow-keys]") !== null;

  // Stop ArrowUp/Down if we're in a container that handles its own navigation
  if (handlesOwnNavigation && (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "Enter")) {
    e.stopPropagation();
    return;
  }
  if (!isTextEntry) {
    return;
  }
  const isMultiLine = target.tagName === "TEXTAREA" || target.isContentEditable;

  // Stop propagation for all printable characters, backspace, delete to prevent
  // Radix's typeahead from activating while typing in an input.
  // Allow modifier key combos (Cmd+A, Cmd+C, etc.) to work normally.
  if ((e.key.length === 1 || e.key === "Backspace" || e.key === "Delete") && !e.metaKey && !e.ctrlKey) {
    e.stopPropagation();
    return;
  }

  // Stop ArrowLeft/Right/Home/End for cursor movement in all text fields
  if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Home" || e.key === "End") {
    e.stopPropagation();
  }

  // Only stop ArrowUp/Down for multi-line text entries (cursor movement between lines)
  if (isMultiLine && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
    e.stopPropagation();
  }
};
const DropdownSettingsButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  variant?: "primary" | "secondary" | "light" | "ghost" | "destructive";
  size?: "sm" | "base";
}>((t0, ref) => {
  const $ = _c(22);
  let className;
  let onClick;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      onClick,
      variant: t1,
      size: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onClick;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    onClick = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const variant = t1 === undefined ? "ghost" : t1;
  const size = t2 === undefined ? "base" : t2;
  let t3;
  if ($[6] !== className) {
    t3 = cx("cursor-pointer px-2", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onClick) {
    t4 = e => {
      e.stopPropagation();
      onClick?.(e);
    };
    $[8] = onClick;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = size === "sm" ? "size-2" : "size-4";
  let t6;
  if ($[10] !== t5) {
    t6 = cx(t5, "shrink-0");
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <RiMore2Fill className={t6} aria-hidden="true" />;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== props || $[15] !== ref || $[16] !== size || $[17] !== t3 || $[18] !== t4 || $[19] !== t7 || $[20] !== variant) {
    t8 = <Button ref={ref} variant={variant} size={size} className={t3} onClick={t4} {...props}>{t7}</Button>;
    $[14] = props;
    $[15] = ref;
    $[16] = size;
    $[17] = t3;
    $[18] = t4;
    $[19] = t7;
    $[20] = variant;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
});
DropdownSettingsButton.displayName = "DropdownSettingsButton";

/** Prevents Radix from stealing focus away from inputs/elements outside the menu. */
function preserveFocus(...menuRoles: string[]) {
  return () => {
    const prev = document.activeElement as HTMLElement | null;
    if (!prev) return;
    const isInsideMenu = prev.closest("[data-radix-menu-content]");
    const isInputLike = prev.tagName === "INPUT" || prev.tagName === "TEXTAREA" || prev.getAttribute("contenteditable") === "true";
    if (isInsideMenu && !isInputLike) return;
    queueMicrotask(() => {
      const now = document.activeElement;
      const role = now?.getAttribute("role");
      if (role && menuRoles.includes(role) && now !== prev) {
        prev.focus();
      }
    });
  };
}
const DropdownMenuSubMenuTrigger = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitives.SubTrigger>, Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.SubTrigger>, "asChild"> & {
  arrowPosition?: "left" | "right" | "none";
  /** Which way the arrow points. Defaults to the side it sits on. */
  arrowDirection?: "left" | "right";
}>(({
  className,
  children,
  arrowPosition = "right",
  arrowDirection = arrowPosition === "left" ? "left" : "right",
  ...props
}, forwardedRef) => {
  const ArrowIcon = arrowDirection === "left" ? RiArrowLeftSLine : RiArrowRightSLine;
  return <DropdownMenuPrimitives.SubTrigger ref={forwardedRef} className={cx(
  // base
  "relative flex cursor-default select-none items-center rounded py-1.5 pl-2 pr-1 text-body outline-none transition-colors data-[state=checked]:font-semibold",
  // text color
  "text-foreground",
  // disabled
  "data-[disabled]:pointer-events-none data-[disabled]:text-foreground-muted data-[disabled]:hover:bg-none",
  // focus
  "data-[state=open]:bg-hover focus-visible:bg-hover",
  // hover
  "hover:bg-hover",
  //
  className)} {...props} onPointerLeave={preserveFocus("menuitem", "menu")} onPointerMove={preserveFocus("menuitem")}>
        {arrowPosition === "left" && <ArrowIcon className="mr-2 size-4 shrink-0" aria-hidden="true" />}
        {children}
        {arrowPosition === "right" && <ArrowIcon className="ml-auto size-4 shrink-0" aria-hidden="true" />}
      </DropdownMenuPrimitives.SubTrigger>;
});
DropdownMenuSubMenuTrigger.displayName = "DropdownMenuSubMenuTrigger";
const DropdownMenuSubMenuContent = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitives.SubContent>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.SubContent>>((t0, forwardedRef) => {
  const $ = _c(15);
  let className;
  let onKeyDownCapture;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      collisionPadding: t1,
      onKeyDownCapture,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onKeyDownCapture;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    onKeyDownCapture = $[2];
    props = $[3];
    t1 = $[4];
  }
  const collisionPadding = t1 === undefined ? 8 : t1;
  let t2;
  if ($[5] !== onKeyDownCapture) {
    t2 = e => {
      onKeyDownCapture?.(e);
      stopRadixMenuKeyNavFromTextEntry(e);
    };
    $[5] = onKeyDownCapture;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className) {
    t3 = cx("relative z-50 overflow-hidden rounded-xl border p-1 shadow-xl shadow-black/[2.5%]", "text-body-lg", "bg-background/70 backdrop-blur-md", "min-w-32", "max-h-[var(--radix-popper-available-height)]", "text-foreground", "border-border", "will-change-[transform,opacity]", "data-[state=closed]:animate-hide", "data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== collisionPadding || $[10] !== forwardedRef || $[11] !== props || $[12] !== t2 || $[13] !== t3) {
    t4 = <DropdownMenuPrimitives.Portal><DropdownMenuPrimitives.SubContent ref={forwardedRef} collisionPadding={collisionPadding} onKeyDownCapture={t2} className={t3} {...props} /></DropdownMenuPrimitives.Portal>;
    $[9] = collisionPadding;
    $[10] = forwardedRef;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
});
DropdownMenuSubMenuContent.displayName = "DropdownMenuSubMenuContent";
const DropdownMenuContent = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitives.Content>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Content>>((t0, forwardedRef) => {
  const $ = _c(21);
  let className;
  let onKeyDownCapture;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      collisionPadding: t2,
      align: t3,
      loop: t4,
      onKeyDownCapture,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onKeyDownCapture;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    className = $[1];
    onKeyDownCapture = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const sideOffset = t1 === undefined ? 8 : t1;
  const collisionPadding = t2 === undefined ? 8 : t2;
  const align = t3 === undefined ? "center" : t3;
  const loop = t4 === undefined ? true : t4;
  let t5;
  if ($[8] !== className) {
    t5 = cx("relative z-50 overflow-hidden rounded-xl border p-1 shadow-xl", "text-body-lg", "min-w-48", "max-h-[var(--radix-popper-available-height)]", "bg-background/70 backdrop-blur-md", "text-foreground", "border-border", "will-change-[transform,opacity]", "data-[state=closed]:animate-hide", "data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade", className);
    $[8] = className;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onKeyDownCapture) {
    t6 = e => {
      onKeyDownCapture?.(e);
      stopRadixMenuKeyNavFromTextEntry(e);
    };
    $[10] = onKeyDownCapture;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== align || $[13] !== collisionPadding || $[14] !== forwardedRef || $[15] !== loop || $[16] !== props || $[17] !== sideOffset || $[18] !== t5 || $[19] !== t6) {
    t7 = <DropdownMenuPrimitives.Portal><DropdownMenuPrimitives.Content ref={forwardedRef} className={t5} sideOffset={sideOffset} align={align} collisionPadding={collisionPadding} loop={loop} onKeyDownCapture={t6} {...props} /></DropdownMenuPrimitives.Portal>;
    $[12] = align;
    $[13] = collisionPadding;
    $[14] = forwardedRef;
    $[15] = loop;
    $[16] = props;
    $[17] = sideOffset;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
});
DropdownMenuContent.displayName = "DropdownMenuContent";
const DropdownMenuItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitives.Item>, Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Item>, "asChild"> & {
  shortcut?: string;
  hint?: string;
  icon?: React.ReactNode;
}>((t0, forwardedRef) => {
  const $ = _c(23);
  let children;
  let className;
  let hint;
  let icon;
  let props;
  let shortcut;
  if ($[0] !== t0) {
    ({
      className,
      shortcut,
      hint,
      children,
      icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = hint;
    $[4] = icon;
    $[5] = props;
    $[6] = shortcut;
  } else {
    children = $[1];
    className = $[2];
    hint = $[3];
    icon = $[4];
    props = $[5];
    shortcut = $[6];
  }
  let t1;
  if ($[7] !== className) {
    t1 = cx("group/DropdownMenuItem relative flex cursor-pointer select-none items-center rounded py-1.5 pl-2 pr-1 text-body outline-none transition-colors data-[state=checked]:font-semibold", "text-foreground", "data-[disabled]:pointer-events-none data-[disabled]:text-foreground-muted data-[disabled]:hover:bg-none", "focus-visible:bg-hover", "hover:bg-hover", className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== icon) {
    t2 = icon && <DropdownMenuIconWrapper>{icon}</DropdownMenuIconWrapper>;
    $[9] = icon;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== hint) {
    t3 = hint && <span className={cx("ml-auto pl-2 text-body text-foreground-muted")}>{hint}</span>;
    $[11] = hint;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== shortcut) {
    t4 = shortcut && <span className={cx("ml-auto pl-2 text-body text-foreground-muted")}>{shortcut}</span>;
    $[13] = shortcut;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== children || $[16] !== forwardedRef || $[17] !== props || $[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
    t5 = <DropdownMenuPrimitives.Item ref={forwardedRef} className={t1} {...props}>{t2}{children}{t3}{t4}</DropdownMenuPrimitives.Item>;
    $[15] = children;
    $[16] = forwardedRef;
    $[17] = props;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
});
DropdownMenuItem.displayName = "DropdownMenuItem";
const DropdownMenuCheckboxItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitives.CheckboxItem>, Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.CheckboxItem>, "asChild"> & {
  shortcut?: string;
  hint?: string;
}>((t0, forwardedRef) => {
  const $ = _c(22);
  let checked;
  let children;
  let className;
  let hint;
  let props;
  let shortcut;
  if ($[0] !== t0) {
    ({
      className,
      hint,
      shortcut,
      children,
      checked,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = children;
    $[3] = className;
    $[4] = hint;
    $[5] = props;
    $[6] = shortcut;
  } else {
    checked = $[1];
    children = $[2];
    className = $[3];
    hint = $[4];
    props = $[5];
    shortcut = $[6];
  }
  let t1;
  if ($[7] !== className) {
    t1 = cx("relative flex cursor-pointer select-none items-center gap-x-2 rounded py-1.5 pl-8 pr-1 text-body outline-none transition-colors data-[state=checked]:font-semibold", "text-foreground", "data-[disabled]:pointer-events-none data-[disabled]:text-foreground-muted data-[disabled]:hover:bg-none", "focus-visible:bg-hover", "hover:bg-hover", className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="absolute left-2 flex size-4 items-center justify-center"><DropdownMenuPrimitives.ItemIndicator><RiCheckLine aria-hidden="true" className="size-full shrink-0 text-foreground" /></DropdownMenuPrimitives.ItemIndicator></span>;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== hint) {
    t3 = hint && <span className={cx("ml-auto text-body font-normal text-foreground-muted")}>{hint}</span>;
    $[10] = hint;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== shortcut) {
    t4 = shortcut && <span className={cx("ml-auto text-body font-normal tracking-widest text-foreground-muted")}>{shortcut}</span>;
    $[12] = shortcut;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== checked || $[15] !== children || $[16] !== forwardedRef || $[17] !== props || $[18] !== t1 || $[19] !== t3 || $[20] !== t4) {
    t5 = <DropdownMenuPrimitives.CheckboxItem ref={forwardedRef} className={t1} checked={checked} {...props}>{t2}{children}{t3}{t4}</DropdownMenuPrimitives.CheckboxItem>;
    $[14] = checked;
    $[15] = children;
    $[16] = forwardedRef;
    $[17] = props;
    $[18] = t1;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
});
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";
const DropdownMenuRadioItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitives.RadioItem>, Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.RadioItem>, "asChild"> & {
  shortcut?: string;
  hint?: string;
}>((t0, forwardedRef) => {
  const $ = _c(20);
  let children;
  let className;
  let hint;
  let props;
  let shortcut;
  if ($[0] !== t0) {
    ({
      className,
      hint,
      shortcut,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = hint;
    $[4] = props;
    $[5] = shortcut;
  } else {
    children = $[1];
    className = $[2];
    hint = $[3];
    props = $[4];
    shortcut = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cx("group/DropdownMenuRadioItem relative flex cursor-pointer select-none items-center gap-x-2 rounded py-1.5 pl-8 pr-1 text-body outline-none transition-colors data-[state=checked]:font-semibold", "text-foreground", "data-[disabled]:pointer-events-none data-[disabled]:text-foreground-muted data-[disabled]:hover:bg-none", "focus-visible:bg-hover", "hover:bg-hover", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="absolute left-2 flex size-4 items-center justify-center"><RiRadioButtonFill aria-hidden="true" className="size-full shrink-0 text-primary group-data-[state=checked]/DropdownMenuRadioItem:flex group-data-[state=unchecked]/DropdownMenuRadioItem:hidden dark:text-primary-400" /><RiCheckboxBlankCircleLine aria-hidden="true" className="size-full shrink-0 text-foreground-muted group-data-[state=unchecked]/DropdownMenuRadioItem:flex group-data-[state=checked]/DropdownMenuRadioItem:hidden" /></span>;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== hint) {
    t3 = hint && <span className={cx("ml-auto text-body font-normal text-foreground-muted")}>{hint}</span>;
    $[9] = hint;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== shortcut) {
    t4 = shortcut && <span className={cx("ml-auto text-body font-normal tracking-widest text-foreground-muted")}>{shortcut}</span>;
    $[11] = shortcut;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== children || $[14] !== forwardedRef || $[15] !== props || $[16] !== t1 || $[17] !== t3 || $[18] !== t4) {
    t5 = <DropdownMenuPrimitives.RadioItem ref={forwardedRef} className={t1} {...props}>{t2}{children}{t3}{t4}</DropdownMenuPrimitives.RadioItem>;
    $[13] = children;
    $[14] = forwardedRef;
    $[15] = props;
    $[16] = t1;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
});
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";
const DropdownMenuLabel = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitives.Label>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Label>>((t0, forwardedRef) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("px-2 py-2 text-caption-strong tracking-wide", "text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== forwardedRef || $[6] !== props || $[7] !== t1) {
    t2 = <DropdownMenuPrimitives.Label ref={forwardedRef} className={t1} {...props} />;
    $[5] = forwardedRef;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DropdownMenuLabel.displayName = "DropdownMenuLabel";
const DropdownMenuSeparator = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitives.Separator>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Separator>>((t0, forwardedRef) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("-mx-1 my-1 h-px border-t border-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== forwardedRef || $[6] !== props || $[7] !== t1) {
    t2 = <DropdownMenuPrimitives.Separator ref={forwardedRef} className={t1} {...props} />;
    $[5] = forwardedRef;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";
const DropdownMenuIconWrapper = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("mr-2", "text-foreground-secondary", "group-data-[disabled]/DropdownMenuItem:text-foreground-muted", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
DropdownMenuIconWrapper.displayName = "DropdownMenuIconWrapper";

/** Lists longer than this get a DropdownMenuSearch above them. */
const DROPDOWN_SEARCH_THRESHOLD = 5;

/**
 * A search box pinned to the top of a scrolling menu. ArrowDown moves into the
 * list and Enter picks the first match.
 */
const DropdownMenuSearch = t0 => {
  const $ = _c(9);
  const {
    value,
    onChange,
    placeholder: t1
  } = t0;
  const placeholder = t1 === undefined ? "Search..." : t1;
  const menuItems = _temp;
  let t2;
  if ($[0] !== onChange) {
    t2 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== value) {
    t3 = e_0 => {
      if (e_0.nativeEvent.isComposing) {
        return;
      }
      if (e_0.key === "ArrowDown") {
        const first = menuItems(e_0.currentTarget)[0];
        if (!first) {
          return;
        }
        e_0.preventDefault();
        first.focus();
      } else {
        if (e_0.key === "Enter" && value.trim()) {
          const first_0 = menuItems(e_0.currentTarget)[0];
          if (!first_0) {
            return;
          }
          e_0.preventDefault();
          first_0.click();
        }
      }
    };
    $[2] = value;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== placeholder || $[5] !== t2 || $[6] !== t3 || $[7] !== value) {
    t4 = <div className="sticky -top-1 z-10 -mx-1 -mt-1 bg-background p-1.5"><Input type="search" icon={RiSearchLine} value={value} onChange={t2} onKeyDown={t3} placeholder={placeholder} aria-label={placeholder} autoFocus={true} autocomplete="off" className="h-8 py-1" data-1p-ignore={true} /></div>;
    $[4] = placeholder;
    $[5] = t2;
    $[6] = t3;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
export { DROPDOWN_SEARCH_THRESHOLD, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSearch, DropdownMenuSeparator, DropdownMenuSubMenu, DropdownMenuSubMenuContent, DropdownMenuSubMenuTrigger, DropdownMenuTrigger, DropdownSettingsButton };
function _temp(input) {
  return Array.from(input.closest("[role=\"menu\"]")?.querySelectorAll("[role=\"menuitem\"]:not([data-disabled])") ?? []);
}